import { isEmpty } from 'lodash-es';
import isNil from 'lodash-es/isNil';
import uniq from 'lodash-es/uniq';
import moment from 'moment-timezone';
import { WeatherModelID } from '../../enums';
export { forecastsToMetricTuple, getMetricToFetch, measurementsToMetricTuple, metricsToDisplayBySpan, spanToUseByDay, useForecastAtDay, useForecastDaySummary, useForecasts } from './useForecasts';
/** @deprecated use models from backend endpoint instead */
const START_OF_ARPEGE = 37;
/** @deprecated use models from backend endpoint instead */
const START_OF_GFS = 115;
/** @deprecated use models from backend endpoint instead */
const forecastModels = ['arome', 'arpege', 'gfs'];
/** @deprecated use models from backend endpoint instead */
const getModel = ({ hoursSinceMidnight, isEurope, isFrance }) => {
    if (isEurope) {
        if (isFrance) {
            if (hoursSinceMidnight < START_OF_ARPEGE) {
                return 'arome';
            }
            else if (hoursSinceMidnight < START_OF_GFS) {
                return 'arpege';
            }
            else {
                return 'gfs';
            }
        }
        else {
            if (hoursSinceMidnight < START_OF_GFS) {
                return 'arpege';
            }
            else {
                return 'gfs';
            }
        }
    }
    else {
        return 'gfs';
    }
};
/** @deprecated use models from backend endpoint instead */
export const getModels = (timezone, since, before) => {
    if (isNil(timezone)) {
        return [];
    }
    const now = moment(Date.now()).startOf('day').utc();
    const sinceMoment = moment.unix(since).tz(timezone);
    const beforeMoment = moment.unix(before).tz(timezone);
    const isEurope = timezone.includes('Europe');
    const isFrance = timezone === 'Europe/Paris';
    return uniq([
        getModel({ hoursSinceMidnight: sinceMoment.diff(now, 'hour'), isEurope, isFrance }),
        getModel({ hoursSinceMidnight: beforeMoment.diff(now, 'hour') - 1, isEurope, isFrance })
    ]).filter((s) => s !== undefined);
};
/** @deprecated remove with wr_forecasts */
export const iconCodes = {
    0: 'tornado',
    1: 'tropicalStorm',
    2: 'hurricane',
    3: 'strongStorms',
    4: 'thunderstorms',
    5: 'rainSnow',
    6: 'rainSleet',
    7: 'wintryMix',
    8: 'freezingDrizzle',
    9: 'drizzle',
    10: 'freezingRain',
    11: 'showers',
    12: 'rain',
    13: 'flurries',
    14: 'snowShowers',
    15: 'blowingDriftingSnow',
    16: 'snow',
    17: 'hail',
    18: 'sleet',
    19: 'blowingDustSandstorm',
    20: 'foggy',
    21: 'haze',
    22: 'smoke',
    23: 'breezy',
    24: 'windy',
    25: 'frigidIceCrystals',
    26: 'cloudy',
    27: 'mostlyCloudyNight',
    28: 'mostlyCloudyDay',
    29: 'partlyCloudyNight',
    30: 'partlyCloudyDay',
    31: 'clear',
    32: 'sunny',
    33: 'fairMostlyClear',
    34: 'fairMostlySunny',
    35: 'mixedRainAndHail',
    36: 'hot',
    37: 'isolatedThunderstorms',
    38: 'scatteredThunderstormsDay',
    39: 'scatteredShowersDay',
    40: 'heavyRain',
    41: 'scatteredSnowShowersDay',
    42: 'heavySnow',
    43: 'blizzard',
    44: 'notAvailable',
    45: 'scatteredShowersNight',
    46: 'scatteredSnowShowersNight',
    47: 'scatteredThunderstormsNight',
    61440: 'wmDayCloudyGusts',
    61442: 'wmDayCloudy',
    61448: 'wmDayRain',
    61453: 'wmDaySunny',
    61457: 'wmCloudyGusts',
    61459: 'wmCloudy',
    61463: 'wmRainMix',
    61465: 'wmRain',
    61466: 'wmShowers',
    61467: 'wmSnow',
    61554: 'wmHot',
    61573: 'wmDayWindy',
    61621: 'wmSleet'
};
const ICON_CODE_VALUES = [
    61440, 61442, 61448, 61453, 61457, 61459, 61463, 61465, 61466, 61467, 61554, 61573, 61621
];
export const DATE_SEGMENT_FORMAT = 'YYYY-MM-DD';
/**
 * Returns an array of model inside the given period given time frames (usually from a backend call)
 * The models are already formatted
 * @param period in unix
 */
export function getWeatherModelsInPeriod(period, timeFrames) {
    const models = [];
    if (!isNil(timeFrames) && !isEmpty(timeFrames)) {
        timeFrames.forEach((frame) => {
            if (parseInt(frame.start, 10) < period.end && parseInt(frame.end, 10) > period.start) {
                const formattedModel = WeatherModelID[frame.modelId].toUpperCase();
                models.push(formattedModel);
            }
        });
    }
    return models;
}
/** Metrics to fetch for a daily summary (daily aggregates) */
export const OVER_THE_DAY_FORECASTS_METRICS = [
    'RR',
    'TN',
    'TX',
    'etp',
    'FF',
    'FXY',
    'icon_code'
];
/** Metrics to fetch for a hourly data */
export const HOURLY_FORECASTS_METRICS = [
    'RR',
    'T',
    'U',
    'etp',
    'FF',
    'FXY',
    'DD',
    'SSI',
    'CLDC',
    'VPD',
    'icon_code'
];
/** All plans included */
export const MAXIMUM_NUMBER_OF_FORECASTS_DAYS = 14;
/** in mm */
const RAINFALLS_THRESHOLD = 0;
/** in kph */
export const DANGEROUS_WIND_GUSTS_THRESHOLD = 40;
/**
 * Will extract periods from forecasts
 * A period is at least two consecutive value above the given threshold
 */
function extractPeriodsAboveThreshold(forecasts, metric, threshold) {
    const periods = [];
    let currentPeriod = {};
    forecasts.forEach((it, i) => {
        const currentValue = it[metric];
        // if we're at the end and there is a period building
        // we add it to the list and end it
        if (i === forecasts.length - 1) {
            if (!isEmpty(currentPeriod)) {
                if (!isNil(currentValue) && currentValue > threshold) {
                    currentPeriod.end = it.timestamp;
                    currentPeriod.values = [...currentPeriod.values, currentValue];
                }
                periods.push(currentPeriod);
                currentPeriod = {};
            }
        }
        else if (i >= 1) {
            const previousValue = forecasts[i - 1][metric];
            // if we have two concurrent values
            if (!isNil(previousValue) &&
                !isNil(currentValue) &&
                previousValue > threshold &&
                currentValue > threshold) {
                // if there is no current we start a new one
                if (isEmpty(currentPeriod)) {
                    currentPeriod.start = forecasts[i - 1].timestamp;
                    currentPeriod.end = it.timestamp;
                    currentPeriod.values = [previousValue, currentValue];
                }
                // if there is one we expand it
                else {
                    currentPeriod.end = it.timestamp;
                    currentPeriod.values = [...currentPeriod.values, currentValue];
                }
            }
            // if there is a current period
            // we add it to the list and end it
            else if (!isEmpty(currentPeriod)) {
                periods.push(currentPeriod);
                currentPeriod = {};
            }
        }
    });
    return periods;
}
/**
 * Will extract periods of rain (values above 0) from a given forecast array
 */
export function extractRainfallsPeriods(items) {
    return extractPeriodsAboveThreshold(items, 'RR', RAINFALLS_THRESHOLD);
}
/**
 * Will extract periods of dangerous winds (values above 40) from a given forecast array
 */
export function extractWindPeriods(items) {
    return extractPeriodsAboveThreshold(items, 'FXY', DANGEROUS_WIND_GUSTS_THRESHOLD);
}
